<template>
  <section class="py-16 md:py-24 bg-base-100">
    <div class="max-w-screen-xl mx-auto px-4">
      <!-- Clean section header -->
      <div class="text-center mb-16">
        <h2 class="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight text-base-content">
          {{ t("deep.heading") }}
        </h2>
        <div class="divider divider-primary w-32 mx-auto mt-6"></div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <!-- FAQ Section -->
        <div>
          <!-- Section title -->
          <h3 class="text-2xl font-bold mb-8 text-base-content">
            {{ t("deep.faqTitle") }}
          </h3>

          <div class="space-y-4">
            <div v-for="i in faqLength" :key="i" class="collapse collapse-arrow bg-base-200">
              <input type="checkbox" :checked="open === i - 1" @change="open = open === i - 1 ? null : i - 1" />
              <div class="collapse-title text-lg font-medium">
                {{ t(`deep.faq[${i - 1}].question`) }}
              </div>
              <div class="collapse-content">
                <p class="text-base-content/80">
                  {{ t(`deep.faq[${i - 1}].answer`) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Professional comparison table (Desktop) -->
        <div class="hidden lg:block relative">
          <!-- Section title -->
          <div class="mb-12">
            <h3 class="text-3xl lg:text-4xl font-bold mb-4 leading-tight text-base-content">
              {{ t("deep.compareTitle") }}
            </h3>
            <p class="text-lg lg:text-xl text-base-content/70 leading-relaxed mb-6">
              {{ t("deep.compareSubtitle") }}
            </p>
          </div>

          <div class="group relative">
            <div
              class="relative bg-white border border-base-200 shadow-lg hover:shadow-xl rounded-2xl transition-all duration-300">
              <div
                class="absolute inset-0 bg-gradient-to-br from-success/3 via-primary/5 to-secondary/3 rounded-2xl pointer-events-none"></div>

              <div class="relative z-10">
                <div class="overflow-hidden rounded-xl border border-base-200">
                  <table class="w-full">
                    <caption class="sr-only">
                      {{
                        t("deep.compareTitle")
                      }}
                    </caption>
                    <thead>
                      <tr class="bg-gradient-to-r from-primary/10 via-success/15 to-secondary/10">
                        <th scope="col" class="p-6 text-left"></th>
                        <th scope="col" class="p-6 text-left">
                          <div class="flex items-center gap-3">
                            <div
                              class="w-8 h-8 bg-gradient-to-br from-success to-success/80 rounded-full flex items-center justify-center">
                              <LucideCheck class="w-4 h-4 text-white" />
                            </div>
                            <span class="text-xl font-bold text-success">
                              {{ t("deep.compare.total") }}
                            </span>
                          </div>
                        </th>
                        <th scope="col" class="p-6 text-left">
                          <div class="flex items-center gap-3">
                            <div
                              class="w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                              <LucideMinus class="w-4 h-4 text-white" />
                            </div>
                            <span class="text-xl font-bold text-gray-500">
                              {{ t("deep.compare.basic") }}
                            </span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <!-- Professional table body -->
                    <tbody>
                      <tr
                        v-for="i in featureLength"
                        :key="i"
                        class="border-b border-base-200 last:border-b-0 hover:bg-gradient-to-r hover:from-primary/3 hover:to-success/3 transition-all duration-300">
                        <td class="p-6">
                          <span class="text-lg font-semibold text-base-content/90">
                            {{ t(`deep.compare.features[${i - 1}].name`) }}
                          </span>
                        </td>
                        <td class="p-6">
                          <div class="flex items-center gap-3">
                            <div
                              class="w-6 h-6 bg-gradient-to-br from-success to-success/80 rounded-full flex items-center justify-center flex-shrink-0">
                              <LucideCheck class="w-3 h-3 text-white" />
                            </div>
                            <span class="text-base font-semibold text-success">
                              {{ t(`deep.compare.features[${i - 1}].total`) }}
                            </span>
                          </div>
                        </td>
                        <td class="p-6">
                          <div class="flex items-center gap-3">
                            <div
                              class="w-6 h-6 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <LucideMinus class="w-3 h-3 text-white" />
                            </div>
                            <span class="text-base text-gray-500">
                              {{ t(`deep.compare.features[${i - 1}].basic`) }}
                            </span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Professional mobile comparison cards -->
        <div class="lg:hidden space-y-8 flex flex-col items-center text-center">
          <!-- Mobile header -->
          <div class="mb-8">
            <h3 class="text-3xl font-bold mb-4 leading-tight text-base-content">
              {{ t("deep.compareTitle") }}
            </h3>
            <p class="text-lg text-base-content/70 leading-relaxed mb-6">
              {{ t("deep.compareSubtitle") }}
            </p>
            <div class="flex items-center justify-center gap-2">
              <div class="w-8 h-0.5 bg-success/60 rounded-full"></div>
              <div class="w-2 h-2 bg-primary/60 rounded-full"></div>
            </div>
          </div>

          <div v-for="i in featureLength" :key="i" class="group relative w-full max-w-md">
            <div
              class="relative bg-white border border-base-200 shadow-lg hover:shadow-xl rounded-2xl p-8 transition-all duration-300">
              <div
                class="absolute inset-0 bg-gradient-to-br from-primary/3 via-success/5 to-secondary/3 rounded-2xl pointer-events-none"></div>

              <div class="relative z-10">
                <div class="mb-6">
                  <h4 class="text-xl font-bold text-base-content/90 leading-snug">
                    {{ t(`deep.compare.features[${i - 1}].name`) }}
                  </h4>
                </div>

                <div class="grid grid-cols-2 gap-6">
                  <div class="text-left">
                    <div class="inline-flex items-center gap-2 mb-3">
                      <div
                        class="w-6 h-6 bg-gradient-to-br from-success to-success/80 rounded-full flex items-center justify-center">
                        <LucideCheck class="w-3 h-3 text-white" />
                      </div>
                      <span
                        class="bg-gradient-to-r from-success/20 to-success/10 text-success text-sm px-3 py-1 rounded-full font-bold">
                        {{ t("deep.compare.total") }}
                      </span>
                    </div>
                    <p class="text-sm text-success font-semibold leading-relaxed">
                      {{ t(`deep.compare.features[${i - 1}].total`) }}
                    </p>
                  </div>

                  <!-- Basic package -->
                  <div class="text-left">
                    <div class="inline-flex items-center gap-2 mb-3">
                      <div
                        class="w-6 h-6 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                        <LucideMinus class="w-3 h-3 text-white" />
                      </div>
                      <span class="bg-gray-100 text-gray-500 text-sm px-3 py-1 rounded-full font-bold">
                        {{ t("deep.compare.basic") }}
                      </span>
                    </div>
                    <p class="text-sm text-gray-500 leading-relaxed">
                      {{ t(`deep.compare.features[${i - 1}].basic`) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { LucideChevronDown, LucideCheck, LucideMinus } from "lucide-vue-next";

export default defineComponent({
  name: "DeepDiveSection",
  components: {
    LucideChevronDown,
    LucideCheck,
    LucideMinus,
  },
  setup() {
    const { t, locale, messages } = useI18n();
    const open = ref<number | null>(null);

    const faqLength = ((messages.value[locale.value] as any)?.deep?.faq?.length as number) || 0;

    const featureLength = ((messages.value[locale.value] as any)?.deep?.compare?.features?.length as number) || 0;

    return {
      t,
      open,
      faqLength,
      featureLength,
    };
  },
});
</script>
